import { useEffect, useState } from 'preact/hooks';
import * as auth from '../lib/auth.js';

/**
 * The hosts' front door.
 *
 * Replaces per-device enrollment, which made identity a property of a phone: a
 * host on a laptop was a stranger, and anyone who opened the installed app
 * without an enrollment link was told the device "isn't set up yet" with nowhere
 * to go. Signing in is the same act on every device, and it can be repeated.
 */
export function SignIn({
  onGuest,
  todayRoomId,
}: {
  onGuest: () => void;
  todayRoomId?: string;
}) {
  const [providers, setProviders] = useState<string[] | null>(null);
  const [error] = useState<string | null>(auth.redirectError());

  useEffect(() => {
    void auth.enabledProviders().then(setProviders);
  }, []);

  const appleReady = providers?.includes('apple') ?? false;

  return (
    <main class="host">
      <header class="host__head">
        <div>
          <p class="host__hello">Waves</p>
          <p class="t-foot">Hosts sign in here.</p>
        </div>
      </header>

      {error ? <p class="notice notice--bad">{error}</p> : null}

      {providers === null ? <p class="t-foot">Checking…</p> : null}

      {providers !== null && appleReady ? (
        <button class="host__go host__apple" onClick={() => auth.signInWithApple('/host')}>
          {/* Apple's mark, inline: one glyph is not worth a font or an image
              request, and this has to render before anything else on the page. */}
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor">
            <path d="M16.36 12.76c.02-2.3 1.88-3.4 1.96-3.45-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.83-.81-3-.79-1.54.02-2.97.9-3.76 2.28-1.6 2.79-.41 6.91 1.15 9.17.76 1.11 1.67 2.35 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.39 0 1.78.74 3 .72 1.24-.02 2.02-1.12 2.78-2.24.88-1.28 1.24-2.53 1.26-2.59-.03-.01-2.42-.93-2.44-3.69zM14.1 5.6c.63-.77 1.06-1.83.94-2.9-.91.04-2.01.61-2.67 1.37-.59.68-1.1 1.76-.96 2.8 1.01.08 2.05-.51 2.69-1.27z" />
          </svg>
          Sign in with Apple
        </button>
      ) : null}

      {providers !== null && !appleReady ? (
        <section class="cell cell--hero stack">
          <h1 class="t-h1">Sign-in isn't switched on yet</h1>
          <p class="t-foot">
            Apple sign-in needs credentials from the Apple Developer account
            before it will work. Until then, hosts can still start a show from a
            device that was set up with an enrollment link.
          </p>
        </section>
      ) : null}

      {/*
        Always offered. This screen is what an installed-app icon opens for
        EVERYONE, hosts and guests alike, so it must never be a dead end for
        someone who only came to listen.
      */}
      <button class="host__archive" onClick={onGuest}>
        {todayRoomId ? "Here to listen? Join tonight's show →" : 'Just here to listen →'}
      </button>
    </main>
  );
}
